import {
  Box,
  Image,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Divider,
  Select,
  Text,
  Link,
  IconButton,
  Card,
  Stack,
  CardBody,
} from "@chakra-ui/react";
import DOMPurify from "dompurify";
import {
  CheckInData,
  DailyCheckIn,
  GetDailyCheckInHistory,
  GetMemberCodeFromQueryString,
  IsMobile,
} from "../service/CommonService";
import React, { useEffect } from "react";
import checked from "../assets/checked.png";
import closeIcon from "../assets/close.png";
import homeIcon from "../assets/home.png";
import historyIcon from "../assets/history.png";
import "../styles/app.css";
import {
  IContent,
  IDailyCheckInRequest,
  IGameData,
  IModalContent,
  IPlayground,
  IPopup,
  IStyle,
  HtmlArrayWithDividerProps,
  IDailyCheckInResult,
  IDailyCheckInHistoryResult,
} from "../interface/Interface";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";

// 根據遊玩天數/第幾天 取得獎項的 Margin 設定
const GetMobileMargin = (index: number, count: number): string => {
  const countRemainder = count % 3;

  const arrCandidate = [0, 3, 6, 9, 12];
  const arr = arrCandidate.filter((m) => m > count - 3);

  if (countRemainder === 0) {
    return "1.5% 1.5% 1.5% 1.5%";
  }

  if (countRemainder === 1) {
    if (arr.includes(index)) {
      return "1.5% 1.5% 1.5% 34.5%";
    }

    return "1.5% 1.5% 1.5% 1.5%";
  }

  if (countRemainder === 2) {
    if (arr.includes(index)) {
      return "1.5% 2.5% 1.5% 18.5%";
    }

    return "1.5% 1.5% 1.5% 1.5%";
  }

  return "";
};

const GetDeskTopMargin = (index: number, count: number): string => {
  const situation1 = [5, 10, 13, 14, 15];
  const situation2 = [8, 9];
  const situation3 = [6, 7];
  const situation4 = [11, 12];

  if (count === 2) {
    if (index == 0) {
      return "2% 3.6% 2% 30.4%";
    }
    return "2% 3.6% 2% 3.6%";
  }

  if (count === 3) {
    if (index == 0) {
      return "2% 3.6% 2% 18.8%";
    }
    return "2% 3.6% 2% 3.6%";
  }

  if (count === 4) {
    if (index == 0) {
      return "2% 3.6% 2% 7.2%";
    }

    return "2% 3.6% 2% 3.6%";
  }

  if (situation1.includes(count)) {
    const remainder = index % 5;

    if (count === 13 && index === 10) {
      return "2% 1.5% 2% 23%";
    }

    if (count == 14 && index == 10) {
      return "2% 1.5% 2% 13.5%";
    }

    if (remainder == 0) {
      return "2% 1.5% 2% 4%";
    }

    if (remainder == 4) {
      return "2% 4% 2% 1.5%";
    }

    return "2% 1.5% 2% 1.5%";
  }

  if (situation2.includes(count)) {
    const remainder = index % 5;

    if (index <= 4) {
      if (remainder == 0) {
        return "2% 1.5% 2% 4%";
      }

      return "2% 1.5% 2% 1.5%";
    }

    if (index == 5) {
      if (count == 8) {
        return "2% 1.5% 2% 23%";
      }

      return "2% 1.5% 2% 13.5%";
    }

    return "2% 1.5% 2% 1.5%";
  }

  if (situation3.includes(count)) {
    const remainder = index % 5;

    if (index <= 3) {
      if (remainder == 0) {
        return "2% 3.6% 2% 7.2%";
      }

      return "2% 3.6% 2% 3.6%";
    }

    if (index == 4) {
      if (count == 6) {
        return "2% 3.6% 2% 30.4%";
      }

      return "2% 3.6% 2% 18.8%";
    }

    return "2% 3.6% 2% 3.6%";
  }

  if (situation4.includes(count)) {
    const remainder = index % 4;

    if (remainder == 0) {
      if (count == 11 && index == 8) {
        return "2% 3.6% 2% 16.8%";
      }

      return "2% 3.6% 2% 7.2%";
    }

    return "2% 3.6% 2% 3.6%";
  }

  return "";
};

// 避免 XSS
const CreateMarkup = (dirty: string): string => {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_ATTR: ["target", "href", "style", "class"], // 允许 target 和 href 属性
  });
};

// 取得不可點擊時反灰的百分比
const GetGrayScale = (
  isActive: boolean,
  isCheckIn: boolean,
  grayscale: number
): string => {
  return isActive || isCheckIn
    ? "grayscale(0)"
    : `grayscale(${grayscale / 100})`;
};

const GetAssetsRootPath = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  const asset = urlParams.get("asset") as string;

  return `${process.env.REACT_APP_ASSETS_DOMAIN}/${process.env.REACT_APP_ASSETS_ROOT_PATH}/${asset}/assets/`;
};

const useLocalStorage = (key: string, initialValue: any) => {
  // 獲取初始值
  const [storedValue, setStoredValue] = React.useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(error);
      return initialValue;
    }
  });

  // 定義設置值的方法
  const setValue = (value: any) => {
    try {
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(error);
    }
  };

  return [storedValue, setValue];
};

const defaultData: IGameData = {
  iOSDownloadLink: "",
  id: 0,
  code: "",
  allowLineShare: false,
  allowFBShare: false,
  allowAndroidDownload: false,
  androidDownloadLink: "",
  allowiOSDownload: false,
  style: {} as IStyle,
  awardList: [],
  timeStamp: "",
  assetsMappingList: {},
  languageList: {},
  contentList: [],
  popup: {} as IPopup,
  redirectUrl: "",
  officialUrl: "",
};

const defaultCurrentContent: IContent = {
  name: "",
  shareText: "",
  gameDesc: "",
  awardDesc: "",
  checkProgressText: "",
  accumulateAwardText: "",
  dailyAwardLanguageList: [],
  popupMessage: "",
  accumulateAwardLanguage: {
    awardName: "",
    seqno: 0,
    category: "",
  },
};

const defaultModalContent: IModalContent = {
  isClosed: true,
  content: "",
};

const defaultDailyCheckInResult: IDailyCheckInResult = {
  allowClose: true,
  message: [],
  isPrizeModal: true,
};

const defaultDailyCheckInHistoryResult: IDailyCheckInHistoryResult = {
  list: [],
  message: "",
};

export const Playground = (props: IPlayground) => {
  const [isModelOpen, setIsModelOpen] = React.useState(false);
  const [modalContent, setModalContent] =
    React.useState<IModalContent>(defaultModalContent);
  const [prizeModalContent, setPrizeModalContent] =
    React.useState<IDailyCheckInResult>(defaultDailyCheckInResult);
  const [currentPrizeIndex, setCurrentPrizeIndex] = React.useState(0);
  const [history, setHistory] = React.useState<IDailyCheckInHistoryResult>(
    defaultDailyCheckInHistoryResult
  );

  const [isPrizeModalOpen, setIsPrizeModalOpen] = React.useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = React.useState(false);

  const [langCode, setLangCode] = useLocalStorage("langCode", "zh-tw");

  const [gameData, setGameData] = React.useState<IGameData>(defaultData);
  
  const [currentContentData, setCurrentContentData] = React.useState<IContent>(
    defaultCurrentContent
  );

  const [isClickAssignButton, setIsClickAssignButton] = React.useState(false);

  let langList: React.ReactNode[] = [];

  Object.entries(gameData.languageList).forEach(([key, value]) => {
    langList.push(
      <option key={key} value={key}>
        {value as string}
      </option>
    );
  });

  const isMobile = IsMobile();
  const days = gameData.awardList.length;

  useEffect(() => {
    if (props.data !== undefined) {
      setGameData(props.data);

      var currentLangCode = langCode; // 使用 localStorage 中的值作為初始值

      //// 目前 LangCode 不在清單內，恢復預設值
      if (Object.keys(props.data.languageList).length > 0 && props.data.languageList[langCode as keyof object] === undefined) {
        currentLangCode = "zh-tw"; // 只有在語系不存在時才設為預設值
        setLangCode(currentLangCode);
      }

      const contentData = props.data.contentList.find(
        (m) => m.languageCode === currentLangCode
      );

      if (contentData !== undefined) {
        setCurrentContentData(contentData.content);
      }
    }

    const checkInData = async () => {
      if (props.data.redirectUrl.length > 0) {
        await CheckInData(props.data.redirectUrl);
      }
    };

    checkInData();

    /// Trigger 錨點
    const element = document.getElementById("current");
    
    const isWYSIWYG =
      new URLSearchParams(window.location.search).get("wysiwyg") === "true";

    if (element && props.data.awardList.length > 9 && isWYSIWYG == false) {
      setTimeout(() => {
        element.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }

    if (props !== undefined && props.data.popup.isPopup === true) {
      const content = props.data.contentList.find(
        (m) => m.languageCode === currentLangCode
      )!.content.popupMessage;

      const modalContent = {
        isClosed: props.data.popup.isAllowClose,
        content: content,
      } as IModalContent;

      setModalContent(modalContent);
      setIsModelOpen(true);
    }
  }, [props, langCode]);

  const GetLanguageDefaultValue = (): string => {
    if (langCode === undefined || langCode === null || langCode === "") {
      setLangCode("zh-tw");
      return "zh-tw";
    }

    return langCode;
  };

  const SwitchLanguageEvent = async (
    event: React.ChangeEvent<HTMLSelectElement>
  ): Promise<void> => {
    setLangCode(event.target.value);

    const contentData = gameData.contentList.find(
      (m) => m.languageCode === event.target.value
    )!;

    setCurrentContentData(contentData.content);
  };

  const LineShareLink = () => {
    return `${process.env.REACT_APP_LINE_SHARE_URL}?text=${encodeURIComponent(
      currentContentData.shareText
    )}&url=${encodeURIComponent(window.location.href)}`;
  };

  const FBShareLink = () => {
    return `${process.env.REACT_APP_FB_URL}?u=${encodeURIComponent(
      window.location.href
    )}`;
  };

  const GetAwardAndOpenModal = async (): Promise<void> => {
    const isWYSIWYG =
      new URLSearchParams(window.location.search).get("wysiwyg") === "true";

    if (isWYSIWYG) {
      return;
    }

    await CheckInData(gameData.redirectUrl);

    const isPreview =
      new URLSearchParams(window.location.search).get("preview") === "true";
    const req = {
      code: gameData.code,
      memberCode: GetMemberCodeFromQueryString(),
      languageCode: langCode,
      isPreview: isPreview,
    } as IDailyCheckInRequest;

    const res = await DailyCheckIn(req);

    setPrizeModalContent(res);
    setCurrentPrizeIndex(0); // <--- 新增
    setIsPrizeModalOpen(true);
  };

  const GetHistory = async (): Promise<void> => {
    const isWYSIWYG =
      new URLSearchParams(window.location.search).get("wysiwyg") === "true";

    if (isWYSIWYG) {
      return;
    }

    await CheckInData(gameData.redirectUrl);

    const isPreview =
      new URLSearchParams(window.location.search).get("preview") === "true";

    const req = {
      code: gameData.code,
      memberCode: GetMemberCodeFromQueryString(),
      languageCode: langCode,
      isPreview: isPreview,
    } as IDailyCheckInRequest;

    const res = await GetDailyCheckInHistory(req);

    setHistory(res);
    setIsHistoryModalOpen(true);
  };

  const HtmlArrayWithDivider: React.FC<HtmlArrayWithDividerProps> = ({
    htmlArray,
  }) => {
    return (
      <Box display="flex" width={"100%"} flexWrap={"wrap"} lineHeight={1}>
        <Box
          width={"100%"}
          display={"flex"}
          justifyContent={"center"}
          flexWrap={"wrap"}
          marginBottom={htmlArray.length > 1 ? "10px" : "0px"}
        >
          {htmlArray.map((htmlString, index) => (
            <React.Fragment key={index}>
              <Box
                hidden={htmlString.title.length == 0}
                width={"100%"}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
              >
                <Box
                  width={isMobile ? "14px" : "16px"}
                  aspectRatio={1}
                  display={"flex"}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Image src={checked} width={"100%"} />
                </Box>
                <Box
                  dangerouslySetInnerHTML={{ __html: htmlString.title }}
                  padding={"0 0 0 5px"}
                  lineHeight={"1.2"}
                />
              </Box>
            </React.Fragment>
          ))}
        </Box>
        <Box
          width={"100%"}
          display={"flex"}
          justifyContent={"center"}
          margin={htmlArray.length > 1 ? "5px" : "2px"}
        >
          {htmlArray.map((htmlString, index) => (
            <React.Fragment key={index}>
              <Box
                width={"100%"}
                padding={"0 15px"}
                lineHeight={"1.2"}
                onClick={(e) => {
                  const btn = (e.target as Element).closest("div[data-action]");

                  if (btn && btn.getAttribute("data-action") === "nextPrize") {
                    setCurrentPrizeIndex(currentPrizeIndex + 1);
                  }
                }}
                dangerouslySetInnerHTML={{ __html: htmlString.paragraph }}
              />
              {index < htmlArray.length - 1 && (
                <Divider
                  orientation={"vertical"}
                  borderColor={gameData.style.popupTextColor}
                  margin={isMobile ? "3px 0" : "0 3px"}
                  height={"auto"}
                />
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    );
  };

  const HtmlArrayWithDividerMobile: React.FC<HtmlArrayWithDividerProps> = ({
    htmlArray,
  }) => {
    return (
      <Box
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        lineHeight={1}
      >
        {htmlArray.map((content, index) => (
          <React.Fragment key={index}>
            {/* title */}
            <Box
              hidden={content.title.length == 0}
              width={"100%"}
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <Box width={isMobile ? "14px" : "16px"} aspectRatio={1}>
                <Image src={checked} width={"100%"} />
              </Box>
              <Box
                dangerouslySetInnerHTML={{ __html: content.title }}
                padding={"0 0 0 5px"}
                lineHeight={"1.2"}
              />
            </Box>
            <Box
              padding={"0 15px"}
              width={"100%"}
              lineHeight={"1.2"}
              onClick={(e) => {
                const btn = (e.target as Element).closest("div[data-action]");

                if (btn && btn.getAttribute("data-action") === "nextPrize") {
                  setCurrentPrizeIndex(currentPrizeIndex + 1);
                }
              }}
              dangerouslySetInnerHTML={{ __html: content.paragraph }}
            />
            {index < htmlArray.length - 1 && (
              <Divider
                orientation={"horizontal"}
                borderColor={gameData.style.popupTextColor}
                margin={"3px 0"}
                height={"auto"}
              />
            )}
          </React.Fragment>
        ))}
      </Box>
    );
  };

  useEffect(() => {
    // 當 title 狀態更新時，設置瀏覽器標籤的 title
    document.title = currentContentData.name;
  }, [currentContentData]);

  let imageList: any[] = [];

  for (let i = 0; i < days; i++) {
    const award = gameData.awardList[i];
    const awardContent = currentContentData.dailyAwardLanguageList[i];

    /// 簽到區塊
    imageList.push(
      <Box
        key={i}
        className="flex-item"
        margin={
          isMobile
            ? GetMobileMargin(i, gameData.awardList.length)
            : GetDeskTopMargin(i, gameData.awardList.length)
        }
        onClick={() => {
          if (award.isActive && isClickAssignButton === false) {
            setIsClickAssignButton(true);
            GetAwardAndOpenModal();
          }
        }}
        style={{ containerType: "inline-size" }}
        cursor={award.isActive ? "pointer" : "default"}
      >
        <Image
          src={GetSignAsset(
            award.sequence,
            props.data.assetsMappingList,
            props.data.timeStamp
          )}
          height={"100%"}
          style={{
            filter: GetGrayScale(
              award.isActive,
              award.isCheckIn,
              gameData.style.disabledGrayScale
            ),
          }}
        />
        <Box
          className={
            award.isActive && gameData.style.breathLight
              ? "breathe-mask flex-item"
              : ""
          }
          zIndex={1000}
        ></Box>
        <Box
          position={"absolute"}
          style={{
            filter: GetGrayScale(
              award.isActive,
              award.isCheckIn,
              gameData.style.disabledGrayScale
            ),
          }}
          width={"100%"}
          height={"20%"}
          top={"0%"}
          color={gameData.style.checkInDateTextColor}
          backgroundColor={gameData.style.checkInDateBGColor}
          fontSize={"15cqw"}
          justifyContent={"center"}
          alignItems={"center"}
          display={"flex"}
        >
          {award.date}
        </Box>
        <Text
          whiteSpace="pre-wrap"
          position={"absolute"}
          style={{
            filter: GetGrayScale(
              award.isActive,
              award.isCheckIn,
              gameData.style.disabledGrayScale
            ),
          }}
          width={"100%"}
          height={"80%"}
          bottom={"0%"}
          color={gameData.style.checkInTextColor}
          backgroundColor={gameData.style.checkInBGColor}
          fontSize={"16cqw"}
          align={"center"}
          padding={"10%"}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          {awardContent.awardDesc}
        </Text>
        {/* 已簽到圖示 */}
        <Box
          position={"absolute"}
          left={"-5px"}
          top={"-3px"}
          width={"25%"}
          hidden={!award.isCheckIn}
        >
          <Image src={checked} />
        </Box>
      </Box>
    );
  }

  const MotionBox = motion(Box);

  return (
    <>
      <Box backgroundColor={gameData.style.backgroundColor} width={"100%"}>
        {/* 視覺區塊 */}
        <Box
          backgroundSize={"cover"}
          backgroundRepeat={"no-repeat"}
          backgroundPosition={"top center"}
          maxWidth={"1920px"}
          width={"100%"}
          height={"100%"}
          margin={"0 auto"}
          justifyContent={"center"}
          alignItems={"flex-start"}
          display={"flex"}
          backgroundImage={
            isMobile
              ? `${GetAssetsRootPath()}${
                  gameData.assetsMappingList["bg_mobile" as keyof Object]
                }?t=${gameData.timeStamp}`
              : `${GetAssetsRootPath()}${
                  gameData.assetsMappingList["bg_web" as keyof Object]
                }?t=${gameData.timeStamp}`
          }
        >
          <Box
            maxWidth={"1000px"}
            width={"100%"}
            justifyItems={"center"}
            minHeight={"100vh"}
          >
            <Box padding={"4px 8px"} float={"right"}>
              <Select
                variant="outline"
                size="xs"
                width={"100px"}
                backgroundColor={"#ffffff"}
                borderWidth={"0px"}
                value={GetLanguageDefaultValue()}
                onChange={SwitchLanguageEvent}
              >
                {langList}
              </Select>
            </Box>
            {/* Title 圖片 */}
            <Box
              width={"100%"}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Image
                src={
                  isMobile
                    ? `${GetAssetsRootPath()}${
                        gameData.assetsMappingList["kv_mobile" as keyof Object]
                      }?t=${gameData.timeStamp}`
                    : `${GetAssetsRootPath()}${
                        gameData.assetsMappingList["kv_web" as keyof Object]
                      }?t=${gameData.timeStamp}`
                }
                objectFit={"cover"}
              />
            </Box>
            {/* 簽到區塊 */}
            <Box
              width={"100%"}
              height={"100%"}
              style={{ containerType: "inline-size" }}
            >
              {/* 簽到進度 */}
              <Box
                fontSize={"5cqw"}
                textAlign={"center"}
                margin={"3% 20% 0% 20%"}
                textColor={gameData.style.mainTextColor}
              >
                {currentContentData.checkProgressText}
              </Box>
              <Box
                fontSize={"4cqw"}
                textAlign={"center"}
                margin={"0% 2% 3% 2%"}
                textColor={gameData.style.mainTextColor}
              >
                {currentContentData.accumulateAwardText}
              </Box>
              <Box padding="2">
                <Divider borderColor={gameData.style.dividerColor} />
              </Box>

              {/* 簽到清單 */}
              <Flex wrap={"wrap"} padding={"2% 0"} id="current">
                {imageList}
              </Flex>
            </Box>
            {/* 遊戲說明 / 獎項說明 */}
            <Box padding={"3%"} width={"100%"}>
              <Box
                minH={"10%"}
                width={"100%"}
                backgroundColor="#FFFFFF"
                dangerouslySetInnerHTML={{
                  __html: `<b>遊戲說明</b>${CreateMarkup(
                    currentContentData.gameDesc
                  )}`,
                }}
                padding={"3%"}
                fontSize={isMobile ? "4vw" : "120%"}
              />
            </Box>
            <Box padding={"0 3% 3% 3%"} width={"100%"}>
              <Box
                minH={"10%"}
                width={"100%"}
                backgroundColor="#FFFFFF"
                dangerouslySetInnerHTML={{
                  __html: `<b>獎項說明</b>${CreateMarkup(
                    currentContentData.awardDesc
                  )}`,
                }}
                padding={"3%"}
                fontSize={isMobile ? "4vw" : "120%"}
              />
            </Box>
            {/* 社群分享 */}
            <Box
              width={"100%"}
              display={"flex"}
              padding={"1.5% 1.5% 0 1.5%"}
              gap={"2%"}
            >
              <Link
                width={"-webkit-fill-available"}
                hidden={!gameData.allowFBShare}
                href={FBShareLink()}
                target="_blank"
              >
                <Image
                  src={`${GetAssetsRootPath()}${
                    gameData.assetsMappingList["fb" as keyof Object]
                  }?t=${gameData.timeStamp}`}
                  objectFit={"cover"}
                  boxSize={"100%"}
                ></Image>
              </Link>
              <Link
                width={"-webkit-fill-available"}
                hidden={!gameData.allowLineShare}
                href={LineShareLink()}
                target="_blank"
              >
                <Image
                  src={`${GetAssetsRootPath()}${
                    gameData.assetsMappingList["line" as keyof Object]
                  }?t=${gameData.timeStamp}`}
                  objectFit={"cover"}
                  boxSize={"100%"}
                ></Image>
              </Link>
            </Box>
            {/* APP 下載 */}
            <Box
              width={"100%"}
              display={"flex"}
              padding={"1.5% 1.5% 1.5% 1.5%"}
              gap={"2%"}
            >
              <Link
                width={"-webkit-fill-available"}
                hidden={!gameData.allowiOSDownload}
                href={gameData.iOSDownloadLink}
                target="_blank"
              >
                <Image
                  src={`${GetAssetsRootPath()}${
                    gameData.assetsMappingList["appstore" as keyof Object]
                  }?t=${gameData.timeStamp}`}
                  objectFit={"cover"}
                  boxSize={"100%"}
                ></Image>
              </Link>
              <Link
                width={"-webkit-fill-available"}
                hidden={!gameData.allowAndroidDownload}
                href={gameData.androidDownloadLink}
                target="_blank"
              >
                <Image
                  src={`${GetAssetsRootPath()}${
                    gameData.assetsMappingList["googleplay" as keyof Object]
                  }?t=${gameData.timeStamp}`}
                  objectFit={"cover"}
                  boxSize={"100%"}
                ></Image>
              </Link>
            </Box>
          </Box>
        </Box>

        {/* 得獎歷程 */}
        <IconButton
          boxShadow={"4px 4px 4px #777777a3"}
          variant="outline"
          width={"65px"}
          height={"65px"}
          colorScheme="#777777"
          backgroundColor={"#EDF2F7"}
          isRound={true}
          position={"fixed"}
          bottom={isMobile ? "90px" : "125px"}
          right={isMobile ? "10px" : "45px"}
          aria-label="得獎紀錄"
          onClick={() => GetHistory()}
          icon={<Image src={historyIcon} width={isMobile ? "42px" : "42px"} />}
        />

        {/* 回首頁 */}
        <IconButton
          boxShadow={"4px 4px 4px #777777a3"}
          variant="outline"
          width={"65px"}
          height={"65px"}
          colorScheme={"#EDF2F7"}
          backgroundColor={"#EDF2F7"}
          isRound={true}
          position={"fixed"}
          bottom={isMobile ? "10px" : "45px"}
          right={isMobile ? "10px" : "45px"}
          aria-label="回官網首頁"
          onClick={() => {
            const isWYSIWYG =
              new URLSearchParams(window.location.search).get("wysiwyg") ===
              "true";

            if (isWYSIWYG) {
              return;
            }

            window.location.href = gameData.officialUrl;
          }}
          icon={<Image src={homeIcon} width={isMobile ? "42px" : "42px"} />}
        />
      </Box>

      {/* 簽到歷程 */}
      <Modal
        isCentered
        onClose={() => {
          setHistory(defaultDailyCheckInHistoryResult);
        }}
        isOpen={isHistoryModalOpen}
        motionPreset="slideInBottom"
        closeOnEsc={false}
        closeOnOverlayClick={false}
      >
        <ModalOverlay backdropFilter="blur(3px)" />
        <ModalContent
          style={
            isMobile
              ? {
                  width: "90vw",
                  height: "85vh",
                  backgroundColor: gameData.style.backgroundColor,
                  boxShadow: "0 0 0 0",
                }
              : {
                  width: "45vw",
                  height: "80vh",
                  backgroundColor: gameData.style.backgroundColor,
                  boxShadow: "0 0 0 0",
                }
          }
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          zIndex={9999}
        >
          <ModalBody style={{ width: "100%", height: "100%", padding: "0" }}>
            <Box
              style={{
                width: "100%",
                height: "100%",
                containerType: "inline-size",
              }}
              flexWrap={"wrap"}
            >
              <Box
                width={"100%"}
                height={"90%"}
                padding={"10px 5px"}
                overflowY={"scroll"}
                position={"absolute"}
                top={"4vh"}
              >
                <Box
                  fontWeight={"bold"}
                  hidden={history.list.length > 0}
                  width={"100%"}
                  height={"100%"}
                  display={"flex"}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  {history.message}
                </Box>
                <Stack hidden={history.list.length == 0}>
                  {history.list.map((item, index) => (
                    <Card
                      direction={{ base: "column", sm: "row" }}
                      overflow="hidden"
                      variant="outline"
                    >
                      <CardBody padding={"5px 10px"} width={"100%"}>
                        <Box display={"flex"} marginBottom={"3px"}>
                          <Box
                            width={"70%"}
                            color={item.isCheckIn ? "#121212" : "#d3d3d3"}
                          >
                            <Box
                              fontSize={isMobile ? "4cqw" : "3cqw"}
                              marginBottom={"3px"}
                            >
                              {item.nthDay}
                            </Box>
                            <Box
                              fontSize={isMobile ? "5cqw" : "4cqw"}
                              noOfLines={1}
                            >
                              {item.name}
                            </Box>
                          </Box>
                          <Box
                            width={"30%"}
                            fontSize={isMobile ? "4cqw" : "3cqw"}
                            color={item.isCheckIn ? "#121212" : "#d3d3d3"}
                          >
                            <Box display={"flex"} justifyContent={"right"}>
                              {item.awardCategory}
                            </Box>
                            <Box display={"flex"} justifyContent={"right"}>
                              {item.isCheckInDesc}
                            </Box>
                          </Box>
                        </Box>
                        <Box
                          display={"flex"}
                          justifyContent={"right"}
                          fontSize={isMobile ? "4cqw" : "3cqw"}
                          marginTop={"5px"}
                        >
                          <Box>
                            {item.awardType === "2" ? (
                              <Text
                                textAlign={"right"}
                                color={item.isSync ? "green" : "blue"}
                              >
                                {item.awardTypeDesc}
                                {item.isSyncDesc}
                              </Text>
                            ) : item.awardType === "1" ? (
                              <Box display="flex" justifyContent={"right"}>
                                <Link
                                  href={item.link}
                                  alignItems={"center"}
                                  display={"flex"}
                                  isExternal
                                >
                                  {item.awardTypeDesc}
                                  <ExternalLinkIcon color={"grey"} />
                                </Link>
                              </Box>
                            ) : (
                              <Box>{item.awardTypeDesc}</Box>
                            )}
                          </Box>
                        </Box>
                      </CardBody>
                    </Card>
                  ))}
                </Stack>
              </Box>
              <Box
                onClick={() => {
                  setIsHistoryModalOpen(false);
                }}
                style={
                  isMobile
                    ? {
                        width: "3vh",
                        position: "absolute",
                        top: "0.5vh",
                        right: "0.5vw",
                      }
                    : {
                        width: "2vw",
                        position: "absolute",
                        top: "0.5vh",
                        right: "0.5vw",
                        maxWidth: "30px",
                      }
                }
                hidden={prizeModalContent.allowClose === false}
              >
                <Image src={`${closeIcon}`}></Image>
              </Box>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Popup 區塊 */}
      <Modal
        isCentered
        onClose={() => {}}
        isOpen={isModelOpen}
        motionPreset="scale"
        closeOnEsc={false}
        closeOnOverlayClick={false}
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(3px)" />
        <ModalContent
          style={
            isMobile
              ? {
                  width: "85vw",
                  height: "127.5vw",
                  backgroundColor: "RGBA(0,0,0,0)",
                  boxShadow: "0 0 0 0",
                }
              : {
                  width: "500px",
                  height: "284px",
                  backgroundColor: "RGBA(0,0,0,0)",
                  boxShadow: "0 0 0 0",
                  maxWidth: "500px",
                }
          }
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          zIndex={9999}
        >
          <ModalBody style={{ width: "100%", height: "100%", padding: "0" }}>
            <Box
              backgroundImage={
                isMobile
                  ? `${GetAssetsRootPath()}${
                      gameData.assetsMappingList[
                        "notstart_mobile" as keyof Object
                      ]
                    }?t=${gameData.timeStamp}`
                  : `${GetAssetsRootPath()}${
                      gameData.assetsMappingList["notstart_web" as keyof Object]
                    }?t=${gameData.timeStamp}`
              }
              style={{
                width: "100%",
                height: "100%",
                containerType: "inline-size",
              }}
              backgroundSize={"contain"}
              backgroundRepeat={"no-repeat"}
              backgroundPosition={"center center"}
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
              flexWrap={"wrap"}
            >
              <Box
                onClick={() => {
                  setIsModelOpen(false);
                }}
                style={
                  isMobile
                    ? {
                        width: "33px",
                        position: "absolute",
                        top: "10px",
                        right: "10px",
                      }
                    : {
                        width: "25px",
                        position: "absolute",
                        top: "10px",
                        right: "15px",
                      }
                }
                hidden={modalContent.isClosed === false}
              >
                <Image src={`${closeIcon}`}></Image>
              </Box>
              <Box
                color={gameData.style.popupTextColor}
                fontWeight={"normal"}
                textAlign={"center"}
                padding={"0 15px"}
                width={"100%"}
                lineHeight={"1.2"}
                dangerouslySetInnerHTML={{
                  __html: `${CreateMarkup(modalContent.content)}`,
                }}
              ></Box>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
      {/* 獎項 Popup 區塊 */}
      <Modal
        isCentered
        onClose={() => {
          if (
            prizeModalContent.message &&
            currentPrizeIndex < prizeModalContent.message.length - 1
          ) {
            setCurrentPrizeIndex(currentPrizeIndex + 1);
          } else {
            setPrizeModalContent(defaultDailyCheckInResult);
            setIsPrizeModalOpen(false);
            window.location.reload();
          }
        }}
        isOpen={isPrizeModalOpen}
        closeOnEsc={false}
        closeOnOverlayClick={false}
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(3px)" />
        <ModalContent
          style={
            isMobile
              ? {
                  width: "85vw",
                  height: "127.5vw",
                  backgroundColor: "RGBA(0,0,0,0)",
                  boxShadow: "0 0 0 0",
                }
              : {
                  width: "500px",
                  height: "284px",
                  backgroundColor: "RGBA(0,0,0,0)",
                  boxShadow: "0 0 0 0",
                  maxWidth: "500px",
                }
          }
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          zIndex={9999}
        >
          <ModalBody style={{ width: "100%", height: "100%", padding: "0" }}>
            <MotionBox
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.4 }}
              backgroundImage={
                isMobile
                  ? prizeModalContent.isPrizeModal
                    ? `${GetAssetsRootPath()}${
                        gameData.assetsMappingList[
                          "prize_win_mobile" as keyof Object
                        ]
                      }?t=${gameData.timeStamp}`
                    : `${GetAssetsRootPath()}${
                        gameData.assetsMappingList[
                          "notstart_mobile" as keyof Object
                        ]
                      }?t=${gameData.timeStamp}`
                  : prizeModalContent.isPrizeModal
                  ? `${GetAssetsRootPath()}${
                      gameData.assetsMappingList[
                        "prize_win_web" as keyof Object
                      ]
                    }?t=${gameData.timeStamp}`
                  : `${GetAssetsRootPath()}${
                      gameData.assetsMappingList["notstart_web" as keyof Object]
                    }?t=${gameData.timeStamp}`
              }
              style={{
                width: "100%",
                height: "100%",
                containerType: "inline-size",
              }}
              backgroundSize={"contain"}
              backgroundRepeat={"no-repeat"}
              backgroundPosition={"center center"}
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
              flexWrap={"wrap"}
            >
              <Box
                onClick={() => {
                  if (
                    prizeModalContent.message &&
                    currentPrizeIndex < prizeModalContent.message.length - 1
                  ) {
                    setCurrentPrizeIndex(currentPrizeIndex + 1);
                  } else {
                    setIsPrizeModalOpen(false);
                    setPrizeModalContent(defaultDailyCheckInResult);
                    window.location.reload();
                  }
                }}
                style={
                  isMobile
                    ? {
                        width: "33px",
                        position: "absolute",
                        top: "10px",
                        right: "10px",
                      }
                    : {
                        width: "25px",
                        position: "absolute",
                        top: "10px",
                        right: "15px",
                      }
                }
                hidden={prizeModalContent.allowClose === false}
              >
                <Image src={`${closeIcon}`}></Image>
              </Box>
              <Box color={gameData.style.popupTextColor} textAlign={"center"}>
                {isMobile ? (
                  <HtmlArrayWithDividerMobile
                    htmlArray={
                      prizeModalContent.message &&
                      prizeModalContent.message.length > 0
                        ? [prizeModalContent.message[currentPrizeIndex]]
                        : []
                    }
                  />
                ) : (
                  <HtmlArrayWithDivider
                    htmlArray={
                      prizeModalContent.message &&
                      prizeModalContent.message.length > 0
                        ? [prizeModalContent.message[currentPrizeIndex]]
                        : []
                    }
                  />
                )}
              </Box>
            </MotionBox>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

function GetSignAsset(
  sequence: number,
  assetsMappingList: Object,
  ts: string
): string {
  const RootPath = GetAssetsRootPath();
  const key = `d${sequence}_sign` as keyof Object;

  const fileName = assetsMappingList[key];

  return `${RootPath}${fileName}?t=${ts}`;
}
